import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { change } from 'redux-form';
import { FormGroup, Label } from 'reactstrap';
import ToggleField from '../ToggleField/ToggleField';
import { Utils } from '../../services';
import {
    availabilitiesFromSlotKeys,
    phaseDays,
    slotKey,
    slotKeysFromAvailabilities,
} from '../../services/availabilitySlots';

import './AvailabilitiesSlotsField.css';

const FORM_NAME = 'formRenderer';
const FIELD_NAME = 'availabilities';

const AvailabilitiesSlotsField = ({
    phases = [],
    periods = [],
    lang,
}) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const uiLang = Utils.i18nUiLang({ language: lang, resolvedLanguage: lang });

    const availabilities = useSelector(
        (state) => state.form?.[FORM_NAME]?.values?.[FIELD_NAME] || []
    );

    const selectedKeys = useMemo(
        () => slotKeysFromAvailabilities(availabilities, phases, periods),
        [availabilities, phases, periods]
    );

    const selectedSet = useMemo(() => new Set(selectedKeys), [selectedKeys]);
    const showPhaseTitles = phases.length > 1;

    const setSlot = (key, checked) => {
        const next = new Set(selectedSet);
        if (checked)
            next.add(key);
        else
            next.delete(key);

        dispatch(change(
            FORM_NAME,
            FIELD_NAME,
            availabilitiesFromSlotKeys([...next], phases, periods)
        ));
    };

    if (!phases?.length || !periods?.length) {
        return (
            <div className="AvailabilitiesSlotsField text-muted">
                {t('forms.availability_slots_empty')}
            </div>
        );
    }

    return (
        <div className="AvailabilitiesSlotsField">
            {phases.map((phase) => {
                const days = phaseDays(phase);
                if (!days.length)
                    return null;

                return (
                    <div key={phase.id} className="availability-slots-phase">
                        {showPhaseTitles && (
                            <div className="availability-slots-phase__title">
                                {Utils.localized_or_fallback(phase.name, uiLang)}
                            </div>
                        )}
                        {days.map((day) => (
                            <FormGroup key={day.format('YYYY-MM-DD')} className="availability-slots-day">
                                <Label className="availability-slots-day__label">
                                    {Utils.formatLocalizedDateTime(day, uiLang, 'dddd LL')}
                                </Label>
                                <div className="availability-slots-toggles">
                                    {periods.map((period) => {
                                        const key = slotKey(phase.id, day, period.id);
                                        const id = `avail-slot-${key}`;
                                        const label = [
                                            Utils.localized_or_fallback(period.name, uiLang),
                                            `${String(period.start_at || '').substring(0, 5)} – ${String(period.stop_at || '').substring(0, 5)}`,
                                        ].filter(Boolean).join(' · ');

                                        return (
                                            <div key={key} className="availability-slots-toggle">
                                                <ToggleField
                                                    standalone
                                                    id={id}
                                                    name={id}
                                                    checked={selectedSet.has(key)}
                                                    onSelectChange={(checked) => setSlot(key, checked)}
                                                />
                                                <Label for={id} className="availability-slots-toggle__label mb-0">
                                                    {label}
                                                </Label>
                                            </div>
                                        );
                                    })}
                                </div>
                            </FormGroup>
                        ))}
                    </div>
                );
            })}
        </div>
    );
};

export default AvailabilitiesSlotsField;
